// Groupes de bénévoles : une identité et une communauté (D-105). Logique pure, testée (volunteer-groups.test.ts) :
// la place de chacun (à l'essai, validé, en pause, ancien ; le décompte de 60 jours, gelé en juillet-août), l'ancienneté
// (saisons, chevrons), le classement du challenge (missions par membre actif), les périodes proposées au bureau.
// Les règles elles-mêmes (délais, transitions) sont tenues par la base (migration 20261031000000) : ici, seulement les mots.

import { parcoursOf, SETTABLE_PARCOURS, visibleParcours } from './badges';

export type PlaceStatus = 'trial' | 'active' | 'paused' | 'former';
export type RewardKind = 'tshirt' | 'soiree' | 'avantage' | 'autre';

/** Ce qui compte comme mission du groupe : son parcours (D-100), en plus des créneaux confiés au groupe. */
export const GROUP_PARCOURS: { key: string; name: string; hint: string }[] = [
  { key: 'buvette', name: 'Buvette et cuisine', hint: 'buvette, cuisine, gâteaux, crêpes' },
  { key: 'logistique', name: 'Logistique', hint: 'installation, rangement, lavage des maillots' },
  { key: 'table', name: 'Table de marque', hint: 'e-marque, chrono, 24 secondes' },
  { key: 'arbitrage', name: 'Arbitrage', hint: 'arbitrer les matchs des jeunes' },
  { key: 'evenements', name: 'Événements', hint: 'tournois, loto, fête du club' },
  { key: 'transport', name: 'Transport', hint: 'conduire aux matchs à l’extérieur' },
  { key: 'encadrement', name: 'Encadrement', hint: 'aide aux coachs, parrainage des jeunes' },
  { key: 'ambassade', name: 'Ambassade', hint: 'recruter, présenter une entreprise' },
];
/** Le nom d'un parcours de groupe, tel que le club l'a réglé (D-127 : renommé, ou créé par le club). */
export const parcoursName = (k: string | null | undefined): string | null =>
  !k ? null : GROUP_PARCOURS.some(p => p.key === k) || parcoursOf(k).custom ? parcoursOf(k).name : null;
/** Les parcours proposés à un groupe (D-127) : ceux de base sous leur nom du club, puis ceux que le club a créés ; pas un
 *  parcours masqué, sauf celui du groupe. */
export function groupParcours(current?: string | null): { key: string; name: string; hint: string }[] {
  const shown = new Set(visibleParcours());
  const base = GROUP_PARCOURS.filter(p => shown.has(p.key) || p.key === current).map(p => ({ ...p, name: parcoursOf(p.key).name }));
  const created = [...shown, ...(current ? [current] : [])].filter((k, i, a) => a.indexOf(k) === i && !SETTABLE_PARCOURS.includes(k) && parcoursOf(k).custom)
    .map(k => ({ key: k, name: parcoursOf(k).name, hint: parcoursOf(k).blurb.toLowerCase() || 'créé par le club' }));
  return [...base, ...created];
}

/** Les emojis proposés (le chef choisit) : ceux du parcours d'abord, puis les classiques. */
const EMOJIS_BY_PARCOURS: Record<string, string[]> = {
  buvette: ['🍳', '🥞', '🍔', '☕', '🧁', '🍕'],
  logistique: ['💪', '🧹', '📦', '🔧', '🪑', '🧺'],
  table: ['🧾', '⏱️', '📋', '🔢', '🖥️', '🔔'],
  arbitrage: ['🧑‍⚖️', '🏁', '🟨', '📯', '🤚', '⚖️'],
  evenements: ['🎉', '📣', '🎟️', '🎤', '🎈', '🏆'],
  transport: ['🚗', '🚐', '🗺️', '⛽', '🚦', '🧭'],
  encadrement: ['🧢', '🏀', '🤝', '🌱', '📣', '⭐'],
  ambassade: ['🤝', '📣', '💼', '🌟', '📸', '✉️'],
};
const EMOJIS_ALL = ['🤝', '🏀', '❤️', '⭐', '🔥', '🎯', '📸', '🎉', '💪', '🍳', '🧾', '🚗', '🏆', '🦁', '🐝', '😎', '🌈', '🚀'];
/** 13 propositions (deux rangées avec la case « ＋ », qui prend n'importe quel emoji du clavier). */
export function groupEmojis(parcours: string | null | undefined, current?: string): string[] {
  const list = [...(parcours ? EMOJIS_BY_PARCOURS[parcours] ?? [] : []), ...EMOJIS_ALL];
  if (current) list.unshift(current);
  return [...new Set(list)].slice(0, 13);
}

/** L'emoji tapé au clavier dans la case « autre » (Idries : « laisse libre de choisir sur son clavier l'émoji qu'on
 *  veut ») : le dernier emoji du texte, entier (drapeaux, teintes, 👩‍🍳) ; les lettres sont ignorées. 8 caractères au
 *  plus, comme la base. */
const PICTO = /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u;
export function typedEmoji(text: string): string | null {
  const parts = typeof Intl !== 'undefined' && 'Segmenter' in Intl
    ? Array.from(new Intl.Segmenter('fr', { granularity: 'grapheme' }).segment(text), s => s.segment)
    : Array.from(text);
  for (let i = parts.length - 1; i >= 0; i--) {
    const g = parts[i]!.trim();
    if (g && PICTO.test(g) && [...g].length <= 8) return g;
  }
  return null;
}

/** Les couleurs du groupe (assez foncées pour un texte blanc dessus). */
export const GROUP_COLORS = ['#C2410C', '#0F4C81', '#1D7A5C', '#6D4BC2', '#B45309', '#BE185D', '#0E7490', '#17191C'];
export const isHexColor = (c: string | null | undefined): c is string => !!c && /^#[0-9A-Fa-f]{6}$/.test(c);

/** « 41 jours », « 1 jour », « dernier jour ». */
export const daysWord = (n: number): string => (n <= 0 ? 'dernier jour' : n === 1 ? '1 jour' : `${n} jours`);

/** « 1re saison », « 2e saison ». */
export const seasonWord = (n: number): string => `${n}${n === 1 ? 're' : 'e'} saison`;

/** Les chevrons de l'ancienneté : un par saison, quatre au plus (au-delà, le chiffre le dit). */
export const chevrons = (n: number): string => '›'.repeat(Math.max(1, Math.min(n, 4)));

/** « 2,4 » : missions par membre actif. */
export const scoreFr = (n: number): string => n.toFixed(1).replace('.', ',');

export interface PlaceLike {
  status: PlaceStatus;
  due?: string | null;
  left?: number | null;
  frozen?: boolean;
  paused_until?: string | null;
  lead?: boolean;
  /** Les délais du club (D-127) : l'essai, le rythme des missions (30 et 60 jours par défaut). */
  trial_days?: number;
  every_days?: number;
}

export type PlaceTone = 'ok' | 'warn' | 'trial' | 'paused' | 'former' | 'late';

/**
 * Les mots de « Ta place dans le groupe » (carte du groupe, accueil Bénévole) :
 *  - à l'essai : « À l'essai : une mission avant le 27 novembre » ;
 *  - validé : « Prochaine mission avant le 8 décembre » (41 jours) ; orange à 14 jours au plus ;
 *  - gel (juillet-août) : le décompte s'arrête ; en pause ; ancien (une mission suffit pour revenir) ;
 *  - le chef garde sa place : en retard, le groupe compte sur lui.
 * `day` met une date ISO en mots (« 8 décembre »). `vous` : un compte partenaire (D-103).
 */
export function placeText(p: PlaceLike, day: (iso: string) => string, vous = false): { tone: PlaceTone; title: string; sub: string; days: number | null } {
  const t = (tu: string, v: string) => (vous ? v : tu);
  const left = p.left ?? null;
  const due = p.due ? day(p.due) : '';
  if (p.status === 'former') return { tone: 'former', title: 'Ancien du groupe', sub: t('Ton historique reste : une mission du groupe suffit pour revenir.', 'Votre historique reste : une mission du groupe suffit pour revenir.'), days: null };
  if (p.status === 'paused') return { tone: 'paused', title: p.paused_until ? `En pause jusqu’au ${day(p.paused_until)}` : 'En pause',
    sub: 'Pas de décompte pendant la pause.', days: null };
  if (p.status === 'trial') return { tone: 'trial', title: `À l’essai : une mission avant le ${due}`,
    sub: t('pour valider ta place dans le groupe', 'pour valider votre place dans le groupe'), days: left };
  if (left !== null && left < 0) return { tone: 'late', title: t('Le groupe compte sur toi', 'Le groupe compte sur vous'), sub: 'Une mission du groupe dès que possible : le chef garde sa place.', days: 0 };
  if (p.frozen) return { tone: 'ok', title: `Prochaine mission avant le ${due}`, sub: 'Décompte gelé en juillet et août.', days: left };
  if (left !== null && left <= 14) return { tone: 'warn', title: left <= 0 ? t('Dernier jour pour garder ta place', 'Dernier jour pour garder votre place') : t(`Plus que ${daysWord(left)} pour garder ta place`, `Plus que ${daysWord(left)} pour garder votre place`),
    sub: `une mission du groupe avant le ${due}`, days: left };
  const every = p.every_days ?? 60;
  return { tone: 'ok', title: `Prochaine mission avant le ${due}`, sub: t(`${daysWord(left ?? every)} · une mission tous les ${every} jours garde ta place`, `${daysWord(left ?? every)} · une mission tous les ${every} jours garde votre place`), days: left };
}

export interface Standing { group_id: string; missions: number; actives: number; score: number; rank: number }
/** Le classement du challenge (comme la base) : missions par membre actif, puis le nombre de missions. */
export function rankStandings<T extends Omit<Standing, 'score' | 'rank'>>(rows: T[]): (T & { score: number; rank: number })[] {
  const scored = rows.map(r => ({ ...r, score: Math.round((r.missions / Math.max(r.actives, 1)) * 10) / 10 }));
  const better = (a: { score: number; missions: number }, b: { score: number; missions: number }) => a.score > b.score || (a.score === b.score && a.missions > b.missions);
  return scored.map(r => ({ ...r, rank: 1 + scored.filter(o => better(o, r)).length }))
    .sort((a, b) => a.rank - b.rank);
}

const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const lastDay = (y: number, m: number) => new Date(y, m, 0).getDate();
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** Les périodes proposées au bureau (objectif, challenge), d'après aujourd'hui (AAAA-MM-JJ). */
export function periodPresets(today: string): { key: string; label: string; from: string; to: string }[] {
  const [y, m] = today.split('-').map(Number) as [number, number];
  const q = Math.floor((m - 1) / 3);
  const qEnd = q * 3 + 3;
  const nm = m === 12 ? 1 : m + 1, ny = m === 12 ? y + 1 : y;
  const seasonEndY = m >= 7 ? y + 1 : y;
  return [
    { key: 'mois', label: `Ce mois-ci (jusqu’au ${lastDay(y, m)} ${MONTHS[m - 1]})`, from: today, to: iso(y, m, lastDay(y, m)) },
    { key: 'suivant', label: `${MONTHS[nm - 1]!.charAt(0).toUpperCase()}${MONTHS[nm - 1]!.slice(1)} (du 1er au ${lastDay(ny, nm)})`, from: iso(ny, nm, 1), to: iso(ny, nm, lastDay(ny, nm)) },
    { key: 'trimestre', label: `Ce trimestre (jusqu’au ${lastDay(y, qEnd)} ${MONTHS[qEnd - 1]})`, from: iso(y, q * 3 + 1, 1), to: iso(y, qEnd, lastDay(y, qEnd)) },
    { key: 'saison', label: 'Jusqu’à la fin de la saison (30 juin)', from: today, to: iso(seasonEndY, 6, 30) },
  ];
}

/** « Novembre », « ce trimestre », « du 3 nov. au 20 déc. » : la période d'un objectif ou d'un challenge. */
export function periodWord(from: string, to: string): string {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  if (fy === ty && fm === tm && fd === 1 && td === lastDay(ty, tm)) return `${MONTHS[fm - 1]!.charAt(0).toUpperCase()}${MONTHS[fm - 1]!.slice(1)}`;
  if (fy === ty && fd === 1 && (fm - 1) % 3 === 0 && tm === fm + 2 && td === lastDay(ty, tm)) return 'Ce trimestre';
  const short = (y: number, mo: number, d: number) => `${d === 1 ? '1er' : d} ${MONTHS_SHORT[mo - 1]}${fy !== ty ? ` ${y}` : ''}`;
  return `Du ${short(fy, fm, fd)} au ${short(ty, tm, td)}`;
}

/** Quand un message du fil a été écrit : « à l'instant », « il y a 5 min », « il y a 3 h », « hier », « lundi », « lundi 26 oct. ». */
export function postWhen(at: Date, now: Date): string {
  const min = Math.round((now.getTime() - at.getTime()) / 60000);
  const day0 = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((day0(now) - day0(at)) / 86400000);
  const wd = at.toLocaleDateString('fr-FR', { weekday: 'long' });
  if (min < 2) return 'à l’instant';
  if (days === 0) return min < 60 ? `il y a ${min} min` : `il y a ${Math.floor(min / 60)} h`;
  if (days === 1) return 'hier';
  if (days < 7) return wd;
  return `${wd} ${at.getDate()} ${MONTHS_SHORT[at.getMonth()]}`;
}

/** « 8 décembre », « 1er décembre » (date ISO). */
export function dayMonth(isoDay: string): string {
  const [, m, d] = isoDay.split('-').map(Number) as [number, number, number];
  return `${d === 1 ? '1er' : d} ${MONTHS[m - 1]}`;
}

export const REWARDS: { kind: RewardKind; icon: string; label: string; fill: (group: string) => string }[] = [
  { kind: 'tshirt', icon: '👕', label: 'T-shirts du groupe', fill: g => `Les t-shirts ${g ? `des ${g.replace(/^(les|la|le|l’|l')\s*/i, '')}` : 'du groupe'}, offerts par le club` },
  { kind: 'soiree', icon: '🍕', label: 'Une soirée', fill: () => 'Une soirée pizza offerte par le club' },
  { kind: 'avantage', icon: '🎁', label: 'Un avantage', fill: () => '' },
  { kind: 'autre', icon: '✏️', label: 'Autre', fill: () => '' },
];
export const rewardIcon = (k: string | null | undefined): string => REWARDS.find(r => r.kind === k)?.icon ?? '🎁';
